import { useEffect, useState } from "react";
import { X, Tag, Hash, Cog, Fuel, Users, Settings as SettingsIcon, ShieldCheck, MapPin, AlertTriangle, ListChecks, Car, ClipboardList, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { getConfirmationTokenVehicle, getPortalBookingVehicle } from "../../lib/vehicleDetails";
import { PublicVehicleGallery } from "../PublicFleetPhotos";
import { VehicleDamageMap } from "../VehicleDamageMap";
import { VIEW_OPTIONS, tagByCode, SEVERITY_DOT_CLASS, detectVehicleType, defaultViewsForType } from "../../lib/damages";
import { ReviewsSection } from "../../pages/RentalProfile";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

/**
 * Renter-facing "View vehicle details": opened from the confirmation page
 * (`source.mode === "token"`) or the authenticated booking detail page
 * (`source.mode === "portal"`). Always tied to one already-authorized
 * booking: the backend derives the vehicle from that booking server-side,
 * never from a client-supplied vehicle id, so this can only ever show the
 * actual booked vehicle. Reuses the same customer-safe field set/components
 * as the public pre-booking vehicle modal and business profile page
 * (gallery, damage map, reviews) rather than a separate renter-only model -
 * it deliberately does NOT reuse `PublicVehicleDetailModal`'s paywalled
 * damage gate, since a confirmed renter already sees active damage for free
 * elsewhere in the app (the confirmation damage sheet).
 */
export default function RenterVehicleDetailModal({ source, onClose }) {
  const { panelRef } = useAccessibleOverlay(true, onClose);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetcher = source.mode === "token"
      ? getConfirmationTokenVehicle(source.token)
      : getPortalBookingVehicle(source.bookingId);
    fetcher
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        const msg = formatApiError(e);
        setError(msg);
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.mode, source.token, source.bookingId]);

  const v = data?.vehicle;
  const vehicleType = detectVehicleType(v?.category);
  const damageViews = defaultViewsForType(vehicleType);
  const [damageView, setDamageView] = useState(null);
  useEffect(() => {
    if (!data?.damages?.length) return;
    const first = data.damages.reduce((acc, d) => acc || d.view, null);
    if (first) setDamageView(first);
  }, [data]);

  const specs = v ? [
    v.make && { icon: <Tag className="w-3.5 h-3.5" />, label: "Make", value: v.make },
    v.model && { icon: <Hash className="w-3.5 h-3.5" />, label: "Model", value: [v.model, v.variant].filter(Boolean).join(" ") },
    v.year && { icon: <Tag className="w-3.5 h-3.5" />, label: "Year", value: v.year },
    v.color && { icon: <Tag className="w-3.5 h-3.5" />, label: "Color", value: v.color },
    v.transmission && { icon: <Cog className="w-3.5 h-3.5" />, label: "Transmission", value: v.transmission },
    v.fuel_type && { icon: <Fuel className="w-3.5 h-3.5" />, label: "Fuel", value: v.fuel_type },
    v.engine_cc && { icon: <Cog className="w-3.5 h-3.5" />, label: "Engine", value: `${v.engine_cc} cc` },
    v.seats && { icon: <Users className="w-3.5 h-3.5" />, label: "Seats", value: v.seats },
    v.doors && { icon: <SettingsIcon className="w-3.5 h-3.5" />, label: "Doors", value: v.doors },
    v.has_aircon && { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Air-conditioning", value: "Yes" },
    v.helmet_included && { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Helmet", value: "Included" },
    v.luggage_capacity && { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Luggage", value: v.luggage_capacity },
    v.plate_number && { icon: <Hash className="w-3.5 h-3.5" />, label: "Plate", value: v.plate_number },
  ].filter(Boolean) : [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto" data-testid="renter-vehicle-detail-modal">
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="renter-vehicle-detail-title"
             className="bg-white rounded-2xl border border-slate-200 max-w-3xl w-full overflow-hidden">
          <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                {v?.category || "Your vehicle"}
              </div>
              <h2 id="renter-vehicle-detail-title" className="font-display text-2xl font-semibold mt-0.5" data-testid="rvdm-title">
                {v?.name || (loading ? "Loading…" : "Vehicle details")}
              </h2>
              {v && (
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                  {v.make && <span><Tag className="w-3 h-3 inline mr-0.5" />{v.make} {v.model} {v.variant}</span>}
                  {v.year && <span>· {v.year}</span>}
                  {v.color && <span>· {v.color}</span>}
                </div>
              )}
            </div>
            <button type="button" onClick={onClose} aria-label="Close vehicle details" data-testid="rvdm-close"
                    className="p-1.5 rounded-md hover:bg-slate-100">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 space-y-6 max-h-[80vh] overflow-y-auto">
            {loading && <div className="text-sm text-slate-500 py-6 text-center">Loading vehicle details…</div>}
            {error && !loading && (
              <div className="text-sm text-rose-600 py-6 text-center" data-testid="rvdm-error">
                Couldn't load vehicle details: {error}
              </div>
            )}

            {v && (
              <>
                <PublicVehicleGallery companyId={data.company_id} vehicleId={v.id} />

                {v.public_description && (
                  <section data-testid="rvdm-description">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">About this vehicle</h3>
                    <p className="mt-1.5 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{v.public_description}</p>
                  </section>
                )}

                {specs.length > 0 && (
                  <section data-testid="rvdm-specs">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Specifications</h3>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {specs.map((s, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 inline-flex items-center gap-1">
                            {s.icon} {s.label}
                          </div>
                          <div className="text-sm font-medium text-slate-900 mt-0.5 capitalize break-words">{s.value}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {(v.public_features || []).length > 0 && (
                  <Section title="Features" testId="rvdm-features" icon={<ListChecks className="w-3.5 h-3.5" />}>
                    <ul className="mt-2 grid sm:grid-cols-2 gap-1 list-disc list-inside text-sm text-slate-700">
                      {v.public_features.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </Section>
                )}

                {(v.included_items || []).length > 0 && (
                  <Section title="Included items" testId="rvdm-included" icon={<ShieldCheck className="w-3.5 h-3.5" />}>
                    <ul className="mt-2 grid sm:grid-cols-2 gap-1 list-disc list-inside text-sm text-slate-700">
                      {v.included_items.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </Section>
                )}

                {v.recommended_use && (
                  <Section title="Recommended use" testId="rvdm-recommended" icon={<Car className="w-3.5 h-3.5" />}>
                    <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-line">{v.recommended_use}</p>
                  </Section>
                )}

                {(v.allowed_travel_area || v.restricted_travel_area || v.pickup_delivery_notes || v.fuel_policy_note || v.mileage_limit) && (
                  <Section title="Pickup, delivery & travel" testId="rvdm-travel" icon={<MapPin className="w-3.5 h-3.5" />}>
                    <dl className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                      {v.pickup_delivery_notes && <Definition label="Pickup / delivery" value={v.pickup_delivery_notes} />}
                      {v.allowed_travel_area && <Definition label="Allowed travel area" value={v.allowed_travel_area} />}
                      {v.restricted_travel_area && <Definition label="Restricted" value={v.restricted_travel_area} />}
                      {v.fuel_policy_note && <Definition label="Fuel policy" value={v.fuel_policy_note} />}
                      {v.mileage_limit && <Definition label="Mileage limit" value={v.mileage_limit} />}
                    </dl>
                  </Section>
                )}

                {(v.requirements_to_rent || v.special_rules) && (
                  <Section title="Rules & requirements" testId="rvdm-rules" icon={<AlertTriangle className="w-3.5 h-3.5" />}>
                    <dl className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                      {v.requirements_to_rent && <Definition label="Requirements" value={v.requirements_to_rent} />}
                      {v.special_rules && <Definition label="Special rules" value={v.special_rules} />}
                    </dl>
                  </Section>
                )}

                {data.damages?.length > 0 && (
                  <section data-testid="rvdm-damage">
                    <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
                      <ClipboardList className="w-3.5 h-3.5" /> Existing damage ({data.damages.length})
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">Pre-existing damage on record for this vehicle. See your booking confirmation for the full acknowledgment flow.</p>
                    {damageView && (
                      <>
                        <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 w-fit mt-2">
                          {damageViews.map((dv) => {
                            const count = data.damages.filter((d) => d.view === dv.value).length;
                            if (count === 0) return null;
                            return (
                              <button key={dv.value} type="button" onClick={() => setDamageView(dv.value)}
                                      data-testid={`rvdm-damage-view-${dv.value}`}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${damageView === dv.value ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"}`}>
                                {dv.label} <span className="text-[10px] text-slate-500">({count})</span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 sm:p-4 mt-2">
                          <VehicleDamageMap vehicleType={vehicleType} view={damageView} damages={data.damages} readOnly />
                        </div>
                      </>
                    )}
                    <ul className="divide-y divide-slate-100 -mx-2 mt-2">
                      {data.damages.map((d, i) => {
                        const tag = tagByCode(d.tag_code);
                        return (
                          <li key={d.id} className="px-2 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid={`rvdm-damage-item-${d.id}`}>
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold">{i + 1}</span>
                            <span className="font-medium">{d.tag_code === "other" ? (d.tag_label || tag.label) : tag.label}</span>
                            <span className={`inline-block w-2 h-2 rounded-full ${SEVERITY_DOT_CLASS[d.severity] || "bg-slate-400"}`} />
                            <span className="text-xs text-slate-500 uppercase tracking-wider">{d.severity}</span>
                            <span className="text-xs text-slate-500">· {VIEW_OPTIONS.find((vv) => vv.value === d.view)?.label || d.view}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                {data.documents?.length > 0 && (
                  <Section title="Documents shared by the business" testId="rvdm-documents" icon={<FileText className="w-3.5 h-3.5" />}>
                    <ul className="mt-2 divide-y divide-slate-100 -mx-2">
                      {data.documents.map((doc) => (
                        <li key={doc.id} className="px-2 py-2 text-sm flex items-center justify-between gap-2 flex-wrap" data-testid={`rvdm-document-${doc.id}`}>
                          <div>
                            <div className="font-medium">{doc.title}</div>
                            <div className="text-xs text-slate-500 capitalize">{(doc.document_type || "").replace(/_/g, " ")}</div>
                          </div>
                          {doc.file_url && (
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" data-testid={`rvdm-document-${doc.id}-link`}
                               className="text-xs text-emerald-700 hover:underline">
                              View document →
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                <ReviewsSection reviews={data.reviews || []} avg={data.reviews_avg} count={data.reviews_count} />
              </>
            )}
          </div>

          <div className="border-t border-slate-200 p-4 flex justify-end">
            <button type="button" onClick={onClose} data-testid="rvdm-back"
                    className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium">
              ← Back to my booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, testId, icon, children }) {
  return (
    <section data-testid={testId}>
      <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
        {icon} {title}
      </h3>
      {children}
    </section>
  );
}

function Definition({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</dt>
      <dd className="text-slate-700 mt-0.5 whitespace-pre-line">{value}</dd>
    </div>
  );
}
